import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Search } from "lucide-react";
import { Logo } from "../components/Logo";
import { OpenBadge } from "../components/OpenBadge";
import { RepairDetail } from "../components/admin/RepairDetail";
import { ChangePassword } from "../components/admin/ChangePassword";
import { PartsManager } from "../components/admin/PartsManager";
import { useAuth } from "../context/AuthContext";
import { http, errMsg, STATUSES, statusOf, fmtDate } from "../lib/api";

export default function Admin() {
  const { logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("queue");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/repairs");
      setItems(Array.isArray(data) ? data : []);
      setErr("");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s.key, items.filter((i) => i.status === s.key).length])), [items]);
  const shown = items.filter((i) => (filter === "all" || i.status === filter) && `${i.ref} ${i.name} ${i.email} ${i.brand_model}`.toLowerCase().includes(q.toLowerCase()));
  const onSaved = (r) => { setItems((p) => p.map((x) => (x.ref === r.ref ? r : x))); setSel(r); };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:px-10">
          <Logo />
          <div className="flex items-center gap-3">
            <OpenBadge className="hidden sm:inline-flex" />
            <a href="/flyer" target="_blank" rel="noreferrer" data-testid="admin-flyer-link" className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 text-sm hover:border-ember">Flyer</a>
            <a href="/cards" target="_blank" rel="noreferrer" data-testid="admin-cards-link" className="hidden items-center gap-2 border border-white/10 px-3 py-2 text-sm hover:border-ember sm:inline-flex">Cards</a>
            <ChangePassword />
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 text-sm hover:border-ember"><LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] px-5 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">Command center</p>
            <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight md:text-5xl">{tab === "queue" ? "Repair queue" : "PC builder parts"}</h1>
          </div>
          {tab === "queue" && <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-bone"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>}
        </div>
        <div className="mt-6 flex gap-1 border-b border-white/[0.08]">
          {[["queue", "Requests"], ["parts", "PC builder parts"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === k ? "border-ember text-bone" : "border-transparent text-slate-400 hover:text-bone"}`}>{l}</button>
          ))}
        </div>
        {tab === "parts" ? <PartsManager /> : (<>

        <div className="mt-8 grid grid-cols-2 gap-px bg-white/[0.06] sm:grid-cols-4 lg:grid-cols-7">
          {STATUSES.map((s) => (
            <button key={s.key} data-testid={`admin-stat-${s.key}`} onClick={() => setFilter(filter === s.key ? "all" : s.key)} className={`bg-ink p-4 text-left transition-colors hover:bg-slab ${filter === s.key ? "bg-slab" : ""}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.label}</p>
              <p className="mt-2 font-display text-3xl font-extrabold">{counts[s.key] ?? 0}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, name, email, model…" className="w-full border border-white/10 bg-white/[0.03] py-3 pl-11 pr-4 text-sm outline-none focus:border-ember" />
          </div>
          <button data-testid="admin-filter-all" onClick={() => setFilter("all")} className={`border px-4 py-3 font-mono text-xs uppercase tracking-wider ${filter === "all" ? "border-ember text-ember" : "border-white/10 text-slate-300"}`}>All ({items.length})</button>
        </div>

        {err && <p data-testid="admin-error" className="mt-6 text-sm text-red-400">{err}</p>}
        <div className="mt-6 border border-white/[0.08]" data-testid="admin-repair-list">
          <div className="hidden grid-cols-12 gap-4 border-b border-white/[0.08] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 md:grid">
            <span className="col-span-2">Ref</span><span className="col-span-3">Customer</span><span className="col-span-3">Device</span><span className="col-span-2">Received</span><span className="col-span-2">Status</span>
          </div>
          {!loading && shown.length === 0 && <p className="p-10 text-center text-sm text-slate-400" data-testid="admin-empty">No repair requests yet.</p>}
          {shown.map((r, i) => {
            const s = statusOf(r.status);
            return (
              <motion.button key={r.ref} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 10) * 0.03 }}
                data-testid={`admin-row-${r.ref}`} onClick={() => setSel(r)}
                className="grid w-full grid-cols-2 gap-2 border-b border-white/[0.06] px-5 py-4 text-left transition-colors hover:bg-white/[0.03] md:grid-cols-12 md:gap-4">
                <span className="font-mono text-sm text-ember md:col-span-2">{r.ref}{r.kind === "build" && <span className="ml-2 bg-ember/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-400">Build</span>}</span>
                <span className="text-right md:order-last md:col-span-2 md:text-left"><span className="inline-block border px-2 py-1 font-mono text-[10px] uppercase tracking-wider" style={{ borderColor: s.color, color: s.color }}>{s.label}</span></span>
                <span className="text-sm md:col-span-3">{r.name}</span>
                <span className="truncate text-right text-sm capitalize text-slate-300 md:col-span-3 md:text-left">{r.device_type} · {r.brand_model}</span>
                <span className="hidden font-mono text-xs text-slate-400 md:col-span-2 md:block">{fmtDate(r.created_at)}</span>
              </motion.button>
            );
          })}
        </div>
        </>)}
      </main>
      <RepairDetail repair={sel} onClose={() => setSel(null)} onSaved={onSaved} />
    </div>
  );
}
